import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Alert,
    AlertTitle,
    Grid,
    Typography,
    Box,
    TextField,
    Radio,
    RadioGroup,
    FormControlLabel,
    Paper,
    Divider,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import SendIcon from '@mui/icons-material/Send';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonIcon from '@mui/icons-material/Person';
import RestoreIcon from '@mui/icons-material/Restore';
import {
    asyncGetSurvey,
    asyncCreateSurveyResponse,
    asyncGetIncompleteSurveyResponse,
    asyncUpdateSurveyResponse,
} from '../../../../../services/survey.service';
import AutocompleteField from '../../../../components/general/fields/autocompleteField';
import MultipleAutocompleteField from '../../../../components/general/fields/multipleAutocompleteField';
import { SurveyResponseStatus } from '../../../../../shared/types/survey-response-status.enum';
import { ToastSwal } from '../../../../components/general/toastSwals/toastSwal';

interface Props {
    onSave: (completed?: boolean) => void;
    onChangeSurvey: (survey: any) => void;
}

interface Answer {
    questionId: string;
    answerId?: string | string[];
    answerText?: string;
    why?: string;
}

interface BrightPatternData {
    caller_id?: string;
    document_type?: string;
    document_number?: string;
    customer_name?: string;
    associated_service?: string;
}

export const FormRender = ({ onSave, onChangeSurvey }: Props) => {
    const params = useParams();
    const {
        id,
        caller_id,
        document_type,
        document_number,
        customer_name,
        associated_service,
    } = params as {
        id: string;
        caller_id?: string;
        document_type?: string;
        document_number?: string;
        customer_name?: string;
        associated_service?: string;
    };
    const [survey, setSurvey] = useState<any>(undefined);
    const [questions, setQuestions] = useState<any[]>([]);
    const [answers, setAnswers] = useState<Record<string, Answer>>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [surveyDisable, setSurveyDisable] = useState<string>('');
    const [bpData, setBpData] = useState<BrightPatternData>({});
    const [incompleteResponse, setIncompleteResponse] = useState<any>(null);
    const [showIncompleteDialog, setShowIncompleteDialog] =
        useState<boolean>(false);
    const [isUpdatingResponse, setIsUpdatingResponse] =
        useState<boolean>(false);

    useEffect(() => {
        // Extraer datos de Bright Pattern desde los parámetros de la ruta
        // Formato: /survey/{id}/{caller_id}/{document_type}/{document_number}/{customer_name}/{associated_service}
        // Usar '-' como placeholder para valores vacíos

        const brightPatternData: BrightPatternData = {
            caller_id:
                caller_id && caller_id !== '-'
                    ? decodeURIComponent(caller_id)
                    : undefined,
            document_type:
                document_type && document_type !== '-'
                    ? decodeURIComponent(document_type)
                    : undefined,
            document_number:
                document_number && document_number !== '-'
                    ? decodeURIComponent(document_number)
                    : undefined,
            customer_name:
                customer_name && customer_name !== '-'
                    ? decodeURIComponent(customer_name)
                    : undefined,
            associated_service:
                associated_service && associated_service !== '-'
                    ? decodeURIComponent(associated_service)
                    : undefined,
        };

        setBpData(brightPatternData);
    }, [
        caller_id,
        document_type,
        document_number,
        customer_name,
        associated_service,
    ]);

    useEffect(() => {
        setLoading(true);
        asyncGetSurvey(id)
            .then((body: any) => {
                if (!body || body.status !== 'active') {
                    setSurvey(null);
                    setSurveyDisable('La encuesta no está disponible.');
                    onChangeSurvey(null);
                } else {
                    setSurvey(body);
                    onChangeSurvey(body);
                    setSurveyDisable('');
                }
            })
            .catch(() => {
                setSurvey(null);
                setSurveyDisable('La encuesta no está disponible.');
                onChangeSurvey(null);
            })
            .finally(() => setLoading(false));
    }, [id]);

    // Buscar encuesta incompleta cuando se carga la encuesta y hay identificador
    useEffect(() => {
        if (survey && (caller_id || document_number)) {
            const identifier =
                document_number && document_number !== '-'
                    ? decodeURIComponent(document_number)
                    : caller_id && caller_id !== '-'
                        ? decodeURIComponent(caller_id)
                        : null;

            if (identifier) {
                asyncGetIncompleteSurveyResponse(id, identifier)
                    .then((response: any) => {
                        if (response) {
                            setIncompleteResponse(response);
                            setShowIncompleteDialog(true);
                        }
                    })
                    .catch((error) => {
                        console.error(
                            'Error al buscar encuesta incompleta:',
                            error
                        );
                    });
            }
        }
    }, [survey, id, caller_id, document_number]);

    const loadIncompleteResponse = () => {
        if (!incompleteResponse) return;

        const loadedAnswers: Record<string, Answer> = {};

        // Cargar las respuestas guardadas
        incompleteResponse.surveyResponseAnswers?.forEach(
            (responseAnswer: any) => {
                const questionId = responseAnswer.question.id;

                if (!loadedAnswers[questionId]) {
                    loadedAnswers[questionId] = {
                        questionId,
                        why: responseAnswer.why || '',
                    };
                }

                // Si tiene texto de respuesta
                if (responseAnswer.answerText) {
                    // Para preguntas tipo linear, el answerText es el valor numérico
                    const question = survey.questions.find(
                        (q: any) => q.id === questionId
                    );
                    if (question?.type === 'linear') {
                        loadedAnswers[questionId].answerId = [
                            responseAnswer.answerText,
                        ];
                    } else {
                        loadedAnswers[questionId].answerText =
                            responseAnswer.answerText;
                    }
                }
                // Si tiene respuesta seleccionada
                else if (responseAnswer.answer) {
                    const answerId = responseAnswer.answer.id;
                    const question = survey.questions.find(
                        (q: any) => q.id === questionId
                    );

                    if (question?.type === 'multiple') {
                        // Para preguntas múltiples, agregar al array
                        if (!loadedAnswers[questionId].answerId) {
                            loadedAnswers[questionId].answerId = [];
                        }
                        (loadedAnswers[questionId].answerId as string[]).push(
                            answerId
                        );
                    } else {
                        // Para preguntas single, guardar como string (NO como array)
                        loadedAnswers[questionId].answerId = answerId;
                    }
                }
            }
        );

        setAnswers(loadedAnswers);
        setIsUpdatingResponse(true);
        setShowIncompleteDialog(false);

        ToastSwal.fire({
            title: 'Encuesta Cargada',
            text: 'Se ha cargado tu encuesta incompleta. Puedes continuar donde lo dejaste.',
            icon: 'info',
            timer: 4000,
            position: 'top-end',
        });
    };

    const startNewResponse = () => {
        setIncompleteResponse(null);
        setShowIncompleteDialog(false);
        setAnswers({});
        setIsUpdatingResponse(false);
    };

    const handleChangeQuestion = (
        questionId: string,
        value: any,
        type: string
    ) => {
        const newAnswer = {
            questionId,
            ...(type === 'multiple'
                ? { answerId: value }
                : type === 'linear' || type === 'single'
                    ? { answerId: value }
                    : { answerText: value }),
            why: answers[questionId]?.why || '',
        };

        const newAnswers = {
            ...answers,
            [questionId]: newAnswer,
        };

        setAnswers(newAnswers);
    };

    const handleChangeWhy = (questionId: string, why: string) => {
        if (answers[questionId]) {
            setAnswers({
                ...answers,
                [questionId]: {
                    ...answers[questionId],
                    why,
                },
            });
        }
    };

    const validateDependency = ({ dependency, position }: any) => {
        const dep = dependency?.questionDependency;
        const depAnswer = dependency?.answerDependency;
        const depAnswerText = dependency?.answerTextDependency;

        // Verificar que la posición de la pregunta dependiente sea mayor
        if (position <= dep?.position) {
            return false;
        }

        const currentAnswer = answers[dep?.id];

        // Si no hay respuesta para la pregunta de la que depende, no mostrar
        if (!currentAnswer) {
            return false;
        }

        // Si no hay answerId ni answerText, no mostrar
        if (!currentAnswer?.answerId && !currentAnswer?.answerText) {
            return false;
        }

        // Si no hay dependencia específica de respuesta, solo verificar que tenga respuesta
        if (
            (!depAnswer || !depAnswer?.id) &&
            (!Boolean(depAnswerText?.trim()) || depAnswerText === '-')
        ) {
            return Boolean(
                currentAnswer?.answerId || currentAnswer?.answerText
            );
        }

        switch (dep?.type) {
            case 'linear':
                const range = depAnswerText.split('-');
                try {
                    const answerValue = parseInt(
                        currentAnswer?.answerId as string
                    );
                    return (
                        parseInt(range[0]) <= answerValue &&
                        parseInt(range[1]) >= answerValue
                    );
                } catch (error) {
                    return false;
                }
            case 'multiple':
                const answerIds = currentAnswer?.answerId;
                if (!Array.isArray(answerIds)) {
                    return false;
                }
                const isSameAnswer = answerIds.includes(depAnswer?.id);
                return (
                    depAnswer?.id &&
                    ((!dependency?.isDiferentOf && isSameAnswer) ||
                        (dependency?.isDiferentOf && !isSameAnswer))
                );
            case 'single':
                return (
                    currentAnswer?.answerId &&
                    depAnswer?.id &&
                    ((!dependency?.isDiferentOf &&
                        currentAnswer?.answerId === depAnswer?.id) ||
                        (dependency?.isDiferentOf &&
                            currentAnswer?.answerId !== depAnswer?.id))
                );
            default:
                return (
                    currentAnswer?.answerId &&
                    depAnswer?.id &&
                    ((!dependency?.isDiferentOf &&
                        currentAnswer?.answerId === depAnswer?.id) ||
                        (dependency?.isDiferentOf &&
                            currentAnswer?.answerId !== depAnswer?.id))
                );
        }
    };

    useEffect(() => {
        const questionsArray = Array.isArray(survey?.questions)
            ? survey.questions
            : [];

        const filteredQuestions = questionsArray.filter(
            ({ questionDependency, position, isOrDependencies }: any) => {
                if (!questionDependency || questionDependency.length === 0) {
                    return true;
                }

                for (const dependency of questionDependency || []) {
                    const isValid = !!validateDependency({
                        dependency,
                        position,
                    });

                    if (isOrDependencies && isValid) {
                        return true;
                    }
                    if (!isOrDependencies && !isValid) {
                        return false;
                    }
                }

                if (isOrDependencies) {
                    return false;
                } else {
                    return true;
                }
            }
        );

        setQuestions(filteredQuestions);
    }, [survey, answers]);

    const isInvalid = () => {
        const requiredQuestions = questions.filter((q) => q.required);
        const missingRequired = requiredQuestions.some(
            (q) => !answers[q.id]?.answerId && !answers[q.id]?.answerText
        );
        return missingRequired;
    };

    const onClickSave = async (status: SurveyResponseStatus) => {
        if (status === SurveyResponseStatus.COMPLETED && isInvalid()) {
            ToastSwal.fire({
                text: 'Por favor completa todas las preguntas obligatorias antes de finalizar la encuesta.',
                icon: 'warning',
                timer: 4000,
                position: 'top-end',
            });
            return;
        }

        setLoading(true);
        try {
            // Preparar las respuestas en el formato correcto
            const response = Object.values(answers).map((answer) => ({
                questionId: answer.questionId,
                answerId: Array.isArray(answer.answerId)
                    ? answer.answerId
                    : answer.answerId
                        ? [answer.answerId]
                        : undefined,
                answerText: answer.answerText || undefined,
                why: answer.why || undefined,
            }));

            let result;

            // Si estamos actualizando una respuesta incompleta existente
            if (isUpdatingResponse && incompleteResponse) {
                result = await asyncUpdateSurveyResponse(
                    incompleteResponse.id,
                    {
                        responses: response,
                        status: status,
                    }
                );
            } else {
                // Crear nueva respuesta
                result = await asyncCreateSurveyResponse({
                    surveyId: id,
                    responses: response,
                    status: status,
                    // Incluir datos de Bright Pattern
                    caller_id: bpData.caller_id,
                    document_type: bpData.document_type,
                    document_number: bpData.document_number,
                    customer_name: bpData.customer_name,
                    associated_service: bpData.associated_service,
                });
            }

            // Mostrar mensaje según el estado guardado
            const isCompleted =
                result?.status === 'completed' ||
                status === SurveyResponseStatus.COMPLETED;

            if (isCompleted) {
                ToastSwal.fire({
                    title: '¡Encuesta Finalizada!',
                    text: 'La encuesta ha sido completada exitosamente. Gracias por tu tiempo.',
                    icon: 'success',
                    timer: 4000,
                    position: 'top-end',
                });
            } else {
                ToastSwal.fire({
                    title: 'Respuestas Guardadas',
                    text: 'Tus respuestas han sido guardadas como incompletas. Puedes continuar más tarde.',
                    icon: 'info',
                    timer: 4000,
                    position: 'top-end',
                });
            }

            // NO recargar la página, solo limpiar el formulario
            setAnswers({});
            setIsUpdatingResponse(false);
            setIncompleteResponse(null);
            window.scrollTo(0, 0);

            // Llamar a onSave con el estado de completado
            if (onSave) {
                onSave(isCompleted || false);
            }
        } catch (error) {
            console.error('Error al guardar respuestas:', error);
            ToastSwal.fire({
                title: 'Error al Guardar',
                text: 'No se pudieron guardar las respuestas. Por favor, intenta de nuevo.',
                icon: 'error',
                timer: 4000,
                position: 'top-end',
            });
        } finally {
            setLoading(false);
        }
    };

    const renderQuestion = (q: any, index: number) => {
        const questionNumber = `${index + 1}. `;
        const isRequired = q.required;

        return (
            <Grid item xs={12} key={q.id}>
                <Paper
                    elevation={2}
                    sx={{
                        p: 3,
                        mx: { xs: 2, md: 8 },
                        mt: index === 0 ? 0 : 3,
                        borderRadius: 2,
                        backgroundColor: '#fafafa',
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: 'Lucida Sans',
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            color: '#333',
                            mb: 2,
                            textAlign: 'center',
                        }}
                    >
                        {questionNumber}
                        {q.description}
                        {isRequired && (
                            <span
                                style={{ color: '#d32f2f', marginLeft: '4px' }}
                            >
                                *
                            </span>
                        )}
                    </Typography>

                    {/* Pregunta de tipo MULTIPLE (selección múltiple) */}
                    {q.type === 'multiple' && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                mt: 2,
                            }}
                        >
                            <Box sx={{ width: '100%', maxWidth: 500 }}>
                                <MultipleAutocompleteField
                                    label="Selecciona una o más opciones"
                                    name={`question_${q.id}`}
                                    data={
                                        q.answers?.map((answer: any) => ({
                                            id: answer.id,
                                            name: answer.description,
                                        })) || []
                                    }
                                    value={
                                        Array.isArray(answers[q.id]?.answerId)
                                            ? (
                                                  answers[q.id]
                                                      ?.answerId as string[]
                                            ).map((id) => ({
                                                id,
                                                name:
                                                      q.answers?.find(
                                                          (a: any) =>
                                                              a.id === id
                                                      )?.description || '',
                                            }))
                                            : []
                                    }
                                    onChangeValue={(selectedAnswers: any[]) => {
                                        const answerIds = selectedAnswers.map(
                                            (item: any) => item.id
                                        );
                                        handleChangeQuestion(
                                            q.id,
                                            answerIds,
                                            'multiple'
                                        );
                                    }}
                                    size="small"
                                />
                            </Box>
                        </Box>
                    )}

                    {/* Pregunta de tipo SINGLE (opción única) */}
                    {q.type === 'single' && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                mt: 2,
                            }}
                        >
                            <Box sx={{ width: '100%', maxWidth: 500 }}>
                                <AutocompleteField
                                    label="Selecciona una opción"
                                    name={`question_${q.id}`}
                                    data={
                                        q.answers?.map((answer: any) => ({
                                            id: answer.id,
                                            name: answer.description,
                                        })) || []
                                    }
                                    value={answers[q.id]?.answerId || ''}
                                    onChangeValue={(value: string) => {
                                        handleChangeQuestion(
                                            q.id,
                                            value,
                                            'single'
                                        );
                                    }}
                                    size="small"
                                />
                            </Box>
                        </Box>
                    )}

                    {/* Pregunta de tipo LINEAR */}
                    {q.type === 'linear' && q.answers?.[0] && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                mt: 2,
                            }}
                        >
                            <RadioGroup
                                row
                                value={answers[q.id]?.answerId || ''}
                                onChange={(e) =>
                                    handleChangeQuestion(
                                        q.id,
                                        e.target.value,
                                        'linear'
                                    )
                                }
                            >
                                {(() => {
                                    const range = q.answers[0].range?.split(
                                        '-'
                                    ) || ['1', '5'];
                                    const start = parseInt(range[0]);
                                    const end = parseInt(range[1]);
                                    const options = [];
                                    for (let i = start; i <= end; i++) {
                                        options.push(
                                            <FormControlLabel
                                                key={i}
                                                value={i.toString()}
                                                control={
                                                    <Radio
                                                        sx={{
                                                            color: 'primary.main',
                                                            '&.Mui-checked': {
                                                                color: 'primary.main',
                                                            },
                                                        }}
                                                    />
                                                }
                                                label={i.toString()}
                                                sx={{ mx: 0.5 }}
                                            />
                                        );
                                    }
                                    return options;
                                })()}
                            </RadioGroup>
                        </Box>
                    )}

                    {/* Pregunta de tipo TEXT */}
                    {q.type === 'text' && (
                        <TextField
                            fullWidth
                            multiline={q.subtype === 'long'}
                            rows={q.subtype === 'long' ? 4 : 1}
                            value={answers[q.id]?.answerText || ''}
                            onChange={(e) =>
                                handleChangeQuestion(
                                    q.id,
                                    e.target.value,
                                    'text'
                                )
                            }
                            placeholder="Escribe tu respuesta aquí"
                            variant="outlined"
                            sx={{
                                mt: 1,
                                '& .MuiOutlinedInput-root': {
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'primary.main',
                                    },
                                },
                            }}
                        />
                    )}

                    {/* Pregunta de tipo DATE */}
                    {q.type === 'date' && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                mt: 2,
                            }}
                        >
                            <TextField
                                type="date"
                                value={answers[q.id]?.answerText || ''}
                                onChange={(e) =>
                                    handleChangeQuestion(
                                        q.id,
                                        e.target.value,
                                        'date'
                                    )
                                }
                                variant="outlined"
                                size="small"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                sx={{
                                    width: 250,
                                    '& .MuiOutlinedInput-root': {
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                    },
                                }}
                            />
                        </Box>
                    )}

                    {/* Campo "¿Por qué?" */}
                    {q.why &&
                        (answers[q.id]?.answerId ||
                            answers[q.id]?.answerText) && (
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={answers[q.id]?.why || ''}
                            onChange={(e) =>
                                handleChangeWhy(q.id, e.target.value)
                            }
                            placeholder="¿Por qué?"
                            label="¿Por qué?"
                            variant="outlined"
                            sx={{
                                mt: 2,
                                '& .MuiOutlinedInput-root': {
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'primary.main',
                                    },
                                },
                            }}
                        />
                    )}
                </Paper>
            </Grid>
        );
    };

    return (
        <>
            {/* Diálogo para encuesta incompleta */}
            <Dialog
                open={showIncompleteDialog}
                onClose={() => setShowIncompleteDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        backgroundColor: '#1976d2',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <RestoreIcon />
                    Encuesta Incompleta Encontrada
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Hemos encontrado una encuesta incompleta que iniciaste
                        anteriormente.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                        <strong>Última actualización:</strong>{' '}
                        {incompleteResponse?.updated_at
                            ? new Date(
                                incompleteResponse.updated_at
                            ).toLocaleString('es-ES', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                            })
                            : 'No disponible'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                        <strong>Preguntas respondidas:</strong>{' '}
                        {incompleteResponse?.surveyResponseAnswers?.length || 0}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        ¿Qué deseas hacer?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button
                        onClick={startNewResponse}
                        variant="outlined"
                        color="secondary"
                    >
                        Empezar de Nuevo
                    </Button>
                    <Button
                        onClick={loadIncompleteResponse}
                        variant="contained"
                        color="primary"
                        startIcon={<RestoreIcon />}
                    >
                        Continuar Encuesta
                    </Button>
                </DialogActions>
            </Dialog>

            <Grid container sx={{ mt: 3, mb: 5 }}>
                {!loading && surveyDisable && (
                    <Grid item xs={12}>
                        <Alert
                            severity="info"
                            iconMapping={{
                                info: (
                                    <InfoOutlinedIcon
                                        sx={{
                                            fontSize: '4rem',
                                            marginBottom: 2,
                                        }}
                                    />
                                ),
                            }}
                            sx={{
                                backgroundColor: 'transparent',
                                color: '#1976d2',
                                textAlign: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '& .MuiAlert-icon': {
                                    margin: 0,
                                    padding: 0,
                                },
                            }}
                        >
                            <AlertTitle
                                sx={{
                                    fontSize: '2.5rem',
                                    fontWeight: 700,
                                    marginBottom: 2,
                                }}
                            >
                                ¡Ups! Lo siento
                            </AlertTitle>

                            <Typography
                                sx={{
                                    fontSize: '1.8rem',
                                    fontWeight: 500,
                                    maxWidth: '800px',
                                }}
                            >
                                {surveyDisable}
                            </Typography>
                        </Alert>
                    </Grid>
                )}

                {/* Datos de Bright Pattern */}
                {survey &&
                    (bpData.caller_id ||
                        bpData.document_number ||
                        bpData.customer_name) && (
                    <Grid item xs={12}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 3,
                                mx: { xs: 2, md: 8 },
                                mb: 3,
                                borderRadius: 2,
                                backgroundColor: '#f3e5f5',
                                border: '2px solid #9c27b0',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <PersonIcon
                                    sx={{
                                        fontSize: '2rem',
                                        color: '#9c27b0',
                                        mr: 1,
                                    }}
                                />
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontFamily: 'Lucida Sans',
                                        fontWeight: 700,
                                        color: '#9c27b0',
                                    }}
                                >
                                        👤 Información del Cliente
                                </Typography>
                            </Box>
                            <Divider
                                sx={{ mb: 2, borderColor: '#9c27b0' }}
                            />
                            <Grid container spacing={2}>
                                {bpData.caller_id && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#666',
                                                fontWeight: 600,
                                            }}
                                        >
                                                📞 Caller ID:
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: '#333',
                                                fontWeight: 500,
                                            }}
                                        >
                                            {bpData.caller_id}
                                        </Typography>
                                    </Grid>
                                )}
                                {bpData.document_type && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#666',
                                                fontWeight: 600,
                                            }}
                                        >
                                                📋 Tipo de Documento:
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: '#333',
                                                fontWeight: 500,
                                            }}
                                        >
                                            {bpData.document_type}
                                        </Typography>
                                    </Grid>
                                )}
                                {bpData.document_number && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#666',
                                                fontWeight: 600,
                                            }}
                                        >
                                                🆔 Número de Documento:
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: '#333',
                                                fontWeight: 500,
                                            }}
                                        >
                                            {bpData.document_number}
                                        </Typography>
                                    </Grid>
                                )}
                                {bpData.customer_name && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#666',
                                                fontWeight: 600,
                                            }}
                                        >
                                                👤 Nombre / Razón Social:
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: '#333',
                                                fontWeight: 500,
                                            }}
                                        >
                                            {bpData.customer_name}
                                        </Typography>
                                    </Grid>
                                )}
                                {bpData.associated_service && (
                                    <Grid item xs={12}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#666',
                                                fontWeight: 600,
                                            }}
                                        >
                                                🏢 Servicio Asociado:
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: '#333',
                                                fontWeight: 500,
                                            }}
                                        >
                                            {bpData.associated_service}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </Paper>
                    </Grid>
                )}

                {/* Guion del Asesor */}
                {survey && survey.advisor_script && (
                    <Grid item xs={12}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 3,
                                mx: { xs: 2, md: 8 },
                                mb: 4,
                                borderRadius: 2,
                                backgroundColor: '#e3f2fd',
                                border: '2px solid #1976d2',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <InfoOutlinedIcon
                                    sx={{
                                        fontSize: '2rem',
                                        color: '#1976d2',
                                        mr: 1,
                                    }}
                                />
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontFamily: 'Lucida Sans',
                                        fontWeight: 700,
                                        color: '#1976d2',
                                    }}
                                >
                                    📋 Guion del Asesor
                                </Typography>
                            </Box>
                            <Typography
                                sx={{
                                    fontFamily: 'Lucida Sans',
                                    fontSize: '1rem',
                                    color: '#333',
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.8,
                                }}
                            >
                                {survey.advisor_script}
                            </Typography>
                        </Paper>
                    </Grid>
                )}

                {survey &&
                    questions.map((q: any, index: number) =>
                        renderQuestion(q, index)
                    )}
                {survey && questions && questions.length > 0 && (
                    <Grid item xs={12} mt={4} sx={{ textAlign: 'center' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                justifyContent: 'center',
                                flexWrap: 'wrap',
                            }}
                        >
                            <LoadingButton
                                loading={loading}
                                disabled={loading}
                                type="button"
                                onClick={() =>
                                    onClickSave(SurveyResponseStatus.INCOMPLETE)
                                }
                                variant="outlined"
                                size="large"
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: '30px',
                                    borderColor: '#1976d2',
                                    color: '#1976d2',
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    ':hover': {
                                        borderColor: '#1565c0',
                                        backgroundColor:
                                            'rgba(25, 118, 210, 0.04)',
                                    },
                                    ':disabled': {
                                        borderColor: '#ccc',
                                        color: '#ccc',
                                    },
                                }}
                            >
                                💾 Guardar Incompleta
                            </LoadingButton>
                            <LoadingButton
                                loading={loading}
                                disabled={isInvalid() || loading}
                                type="submit"
                                onClick={() =>
                                    onClickSave(SurveyResponseStatus.COMPLETED)
                                }
                                variant="contained"
                                endIcon={<SendIcon />}
                                size="large"
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: '30px',
                                    backgroundColor: '#6a1b9a',
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    boxShadow: 3,
                                    ':hover': {
                                        backgroundColor: '#4a148c',
                                        boxShadow: 6,
                                    },
                                    ':disabled': {
                                        backgroundColor: '#ccc',
                                    },
                                }}
                            >
                                ✅ Finalizar Encuesta
                            </LoadingButton>
                        </Box>
                        {isInvalid() && (
                            <Typography
                                variant="caption"
                                sx={{
                                    display: 'block',
                                    mt: 2,
                                    color: '#d32f2f',
                                    fontSize: '0.9rem',
                                }}
                            >
                                * Para finalizar la encuesta, debes completar
                                todas las preguntas obligatorias
                            </Typography>
                        )}
                    </Grid>
                )}
            </Grid>
        </>
    );
};
